import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_e7a0bc96 from 'nuxt_plugin_plugin_e7a0bc96' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_pluginutils_a6998d60 from 'nuxt_plugin_pluginutils_a6998d60' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_02cab29b from 'nuxt_plugin_pluginrouting_02cab29b' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_0425b6b4 from 'nuxt_plugin_pluginmain_0425b6b4' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_axios_cfd2176a from 'nuxt_plugin_axios_cfd2176a' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_bugsnagclient840b4f86_80644cce from 'nuxt_plugin_bugsnagclient840b4f86_80644cce' // Source: ./bugsnag.client.840b4f86.js (mode: 'client')
import nuxt_plugin_nuxtplugin644776d5_e6913b28 from 'nuxt_plugin_nuxtplugin644776d5_e6913b28' // Source: ./nuxt.plugin.644776d5.js (mode: 'all')
import nuxt_plugin_workbox_6ffb82a7 from 'nuxt_plugin_workbox_6ffb82a7' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_7f5732ba from 'nuxt_plugin_metaplugin_7f5732ba' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_gtm_514e51e1 from 'nuxt_plugin_gtm_514e51e1' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_veevalidate_1a0c1998 from 'nuxt_plugin_veevalidate_1a0c1998' // Source: ../../plugins/vee-validate.js (mode: 'all')
import nuxt_plugin_axios_5659d192 from 'nuxt_plugin_axios_5659d192' // Source: ../../plugins/axios.js (mode: 'all')
import nuxt_plugin_vclipboardclient_6eaf7e67 from 'nuxt_plugin_vclipboardclient_6eaf7e67' // Source: ../../plugins/v-clipboard.client.js (mode: 'client')
import nuxt_plugin_vuelazyload_d07c1d40 from 'nuxt_plugin_vuelazyload_d07c1d40' // Source: ../../plugins/vue-lazyload.js (mode: 'all')
import nuxt_plugin_cookieconsent_3dcc05f0 from 'nuxt_plugin_cookieconsent_3dcc05f0' // Source: ../../plugins/cookie-consent.js (mode: 'all')
import nuxt_plugin_vuecheckview_535e18b0 from 'nuxt_plugin_vuecheckview_535e18b0' // Source: ../../plugins/vue-check-view.js (mode: 'client')
import nuxt_plugin_vuescrollto_44ce9a1c from 'nuxt_plugin_vuescrollto_44ce9a1c' // Source: ../../plugins/vue-scrollto.js (mode: 'client')
import nuxt_plugin_vuedragscroll_61ed6ece from 'nuxt_plugin_vuedragscroll_61ed6ece' // Source: ../../plugins/vue-dragscroll.js (mode: 'client')
import nuxt_plugin_vue2datepicker_4e082fe0 from 'nuxt_plugin_vue2datepicker_4e082fe0' // Source: ../../plugins/vue2-datepicker.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Gladwell Academy","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Gladwell Academy trains professionals at the intersection of management, IT and innovation. View our Agile & SAFe training portfolio."},{"hid":"author","name":"author","content":"Gladwell Academy"}],"link":[],"style":[{"hid":"css-variables","innerHTML":":root { --color-darkest:#000000;--color-lightest:#fff;--color-primary:#008FDF;--color-primary-light:#01A4FF;--color-primary-dark:#006BA7;--color-secondary:#001531;--color-secondary-light:#001736;--color-secondary-dark:#001025;--color-gray-100:#F0F5F8;--color-gray-200:#F0F5F8;--color-gray-300:#DEE3E7;--color-gray-400:#B0B9C1;--color-gray-500:#717C87;--color-gray-600:#5a5a5a;--color-gray-700:#5a5a5a;--color-gray-800:#4B575F;--color-gray-900:#4B575F;--color-highlight-success:#418D52;--color-highlight-info:lightblue;--color-highlight-warning:orange;--color-highlight-error:#C53C29;--color-highlight-notice:#F68300;--color-dark-blue:#001531;--color-primary-fresh:#00A3FF;--color-primary-contrast:#0075B7;--color-program-gladwell:#008FDF;--color-program-scaled-agile:#359E7F;--color-program-scrum-org:#C53C29;--color-program-icagile:#8559CC;--color-program-continuous-innovation:#BD7018;--color-black-10:rgba(75, 87, 95, 0.08);--color-black-20:rgba(75, 87, 95, 0.16);--color-black-30:rgba(75, 87, 95, 0.24);--color-black-50:rgba(75, 87, 95, 0.56);--color-black-80:rgba(75, 87, 95, 0.8);--color-black-100:var(--color-secondary);--color-white-10:rgba(255, 255, 255, 0.08);--color-white-20:rgba(255, 255, 255, 0.16);--color-white-30:rgba(255, 255, 255, 0.24);--color-white-50:rgba(255, 255, 255, 0.56);--color-white-80:rgba(255, 255, 255, 0.8);--color-white-100:var(--color-lightest);--transition-timing-slow:1s;--transition-timing-medium:500ms;--transition-timing-fast:150ms;--transition-timingFunction-in:cubic-bezier(0.4, 0, 1, 1);--transition-timingFunction-out:cubic-bezier(0, 0, 0.2, 1);--transition-timingFunction-in-out:cubic-bezier(0.4, 0, 0.2, 1);--transition-fast:var(--transition-timing-fast) 0ms var(--transition-timingFunction-in);--transition-medium:var(--transition-timing-medium) 0ms var(--transition-timingFunction-in);--transition-slow:var(--transition-timing-slow) 0ms var(--transition-timingFunction-in);--transition-page:opacity var(--transition-timing-fast) var(--transition-timingFunction-out);--transition-link:color var(--transition-fast);--spacing-2xs:0.5rem;--spacing-xs:0.75rem;--spacing-sm:1rem;--spacing-md:1.25rem;--spacing-lg:1.5rem;--spacing-xl:2rem;--spacing-2xl:2.5rem;--spacing-4xs:0.125rem;--spacing-3xs:0.25rem;--spacing-3xl:3rem;--spacing-4xl:4rem;--spacing-5xl:5rem;--spacing-6xl:7rem;--grid-side-gap-sm:var(--spacing-sm);--grid-side-gap-md:var(--spacing-lg);--grid-side-gap-lg:var(--grid-gap-lg);--grid-gap-sm:var(--spacing-xl);--grid-gap-md:var(--spacing-2xl);--grid-gap-lg:var(--spacing-3xl);--grid-gap-desktop:var(--grid-gap-lg);--grid-gap-tablet:var(--grid-gap-md);--grid-gap-mobile:var(--grid-gap-sm);--grid-maxWidth-page:71.25rem;--grid-maxWidth-content:46.625rem;--grid-header-height:80px;--grid-header-height-mobile:64px;--grid-header-height-negative:-80px;--grid-header-height-mobile-negative:-64px;--borderRadius-sm:0;--borderRadius-md:0.25rem;--borderRadius-lg:0.5rem;--borderRadius-xl:0.75rem;--borderRadius-full:50%;--boxShadow-light:0px 0px 8px rgba(0, 0, 0, 0.06);--boxShadow-base:0px 0px 6px rgba(0, 0, 0, 0.16);--boxShadow-medium:0px 0px 11px rgba(0, 0, 0, 0.20);--boxShadow-heavy:0px 0px 16px rgba(0, 0, 0, 0.24);;--boxShadow-outline:0 0 0 3px rgba(66, 153, 225, 0.5);--boxShadow-inset:inset 1px 1px 4px rgba(0, 0, 0, 0.12);--font-family-primary:DM Sans, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, \"Noto Sans\", sans-serif;--font-family-secondary:Source Serif Pro, Georgia, Times New Roman, serif;--font-size-xs:0.875rem;--font-size-sm:1rem;--font-size-md:1.125rem;--font-size-lg:1.25rem;--font-size-xl:1.5rem;--font-size-2xl:1.75rem;--font-size-3xl:2.5rem;--font-size-4xl:3.115rem;--font-size-5xl:3.75rem;--font-size-6xl:5rem;--font-size-2xs:0.75rem;--font-size-7xl:6.25rem;--font-weight-primary-hairline:100;--font-weight-primary-thin:200;--font-weight-primary-light:300;--font-weight-primary-normal:400;--font-weight-primary-medium:500;--font-weight-primary-semibold:500;--font-weight-primary-bold:700;--font-weight-primary-extrabold:800;--font-weight-primary-black:900;--font-weight-secondary-hairline:100;--font-weight-secondary-thin:200;--font-weight-secondary-light:300;--font-weight-secondary-normal:400;--font-weight-secondary-medium:500;--font-weight-secondary-semibold:600;--font-weight-secondary-bold:700;--font-weight-secondary-extrabold:800;--font-weight-secondary-black:900;--lineHeight-xs:.9;--lineHeight-sm:1;--lineHeight-md:1.3;--lineHeight-lg:1.5;--lineHeight-xl:2; }"}],"script":[{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-NGPHKRF')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-NGPHKRF&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"],"css-variables":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_e7a0bc96 === 'function') {
    await nuxt_plugin_plugin_e7a0bc96(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_a6998d60 === 'function') {
    await nuxt_plugin_pluginutils_a6998d60(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_02cab29b === 'function') {
    await nuxt_plugin_pluginrouting_02cab29b(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_0425b6b4 === 'function') {
    await nuxt_plugin_pluginmain_0425b6b4(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_cfd2176a === 'function') {
    await nuxt_plugin_axios_cfd2176a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bugsnagclient840b4f86_80644cce === 'function') {
    await nuxt_plugin_bugsnagclient840b4f86_80644cce(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtplugin644776d5_e6913b28 === 'function') {
    await nuxt_plugin_nuxtplugin644776d5_e6913b28(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_6ffb82a7 === 'function') {
    await nuxt_plugin_workbox_6ffb82a7(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_7f5732ba === 'function') {
    await nuxt_plugin_metaplugin_7f5732ba(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_514e51e1 === 'function') {
    await nuxt_plugin_gtm_514e51e1(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_1a0c1998 === 'function') {
    await nuxt_plugin_veevalidate_1a0c1998(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_5659d192 === 'function') {
    await nuxt_plugin_axios_5659d192(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vclipboardclient_6eaf7e67 === 'function') {
    await nuxt_plugin_vclipboardclient_6eaf7e67(app.context, inject)
  }

  if (typeof nuxt_plugin_vuelazyload_d07c1d40 === 'function') {
    await nuxt_plugin_vuelazyload_d07c1d40(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieconsent_3dcc05f0 === 'function') {
    await nuxt_plugin_cookieconsent_3dcc05f0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuecheckview_535e18b0 === 'function') {
    await nuxt_plugin_vuecheckview_535e18b0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuescrollto_44ce9a1c === 'function') {
    await nuxt_plugin_vuescrollto_44ce9a1c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuedragscroll_61ed6ece === 'function') {
    await nuxt_plugin_vuedragscroll_61ed6ece(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vue2datepicker_4e082fe0 === 'function') {
    await nuxt_plugin_vue2datepicker_4e082fe0(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
